// Centralized Yandex Metrica configuration
const DEFAULT_YANDEX_METRIKA_ID = 104582235;

// CRA (react-scripts) replaces `process.env.REACT_APP_*` at build time.
// Use direct access (no typeof guards), otherwise the replacement may be bypassed.
const __RAW_ENV_METRIKA_ID__ = String(process.env.REACT_APP_YANDEX_METRIKA_ID || '').trim();

function parseMetrikaId(raw) {
  try {
    const n = Number(String(raw || '').trim());
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.floor(n);
  } catch {
    return null;
  }
}

const __ENV_METRIKA_ID__ = parseMetrikaId(__RAW_ENV_METRIKA_ID__);

export const YANDEX_METRIKA_ID = __ENV_METRIKA_ID__ ?? DEFAULT_YANDEX_METRIKA_ID;

// Dev-only sanity logs (no spam)
try {
  const isDev = process.env.NODE_ENV !== 'production';
  if (isDev && !__RAW_ENV_METRIKA_ID__) {
    // eslint-disable-next-line no-console
    console.info(`[YM] REACT_APP_YANDEX_METRIKA_ID is not set; using default counter id ${DEFAULT_YANDEX_METRIKA_ID}`);
  }
  if (isDev && __RAW_ENV_METRIKA_ID__ && !__ENV_METRIKA_ID__) {
    // eslint-disable-next-line no-console
    console.warn(`[YM] REACT_APP_YANDEX_METRIKA_ID is invalid (${JSON.stringify(__RAW_ENV_METRIKA_ID__)}); using default counter id ${DEFAULT_YANDEX_METRIKA_ID}`);
  }
} catch {
  // ignore
}

// Single source of truth for all goal names (snake_case)
export const METRIKA_GOALS = Object.freeze({
  // Funnel: entry/auth
  view_welcome: 'view_welcome',
  view_login: 'view_login',

  // Funnel: registration/login
  registration_started: 'registration_started',
  registration_verified: 'registration_verified',
  registration_error: 'registration_error',
  login_success: 'login_success',
  login_error: 'login_error',

  // Monetization funnel. Keep this list intentionally small: billing retention,
  // refunds and renewals are authoritative in the billing database, not Metrika.
  billing_view: 'billing_view',
  checkout_started: 'checkout_started',
  trial_started: 'trial_started',
  payment_succeeded: 'payment_succeeded',

  // Primary acquisition event for the independently indexable Code landing.
  // Page views describe interest; this click is the closest reliable web signal
  // to an installer download starting (the binary is served by another host).
  code_download_clicked: 'code_download_clicked',

  // Web intent only: neither a store click nor release news confirms an install.
  mobile_store_clicked: 'mobile_store_clicked',
  mobile_release_updates_clicked: 'mobile_release_updates_clicked',

  // Activation / product
  record_created: 'record_created',
  record_updated: 'record_updated',
  chat_message_sent: 'chat_message_sent',
  analytics_view: 'analytics_view',

  // Calendar
  calendar_event_created: 'calendar_event_created',
  calendar_event_completed: 'calendar_event_completed',
  calendar_event_deleted: 'calendar_event_deleted',

  // Tracker
  tracker_view: 'tracker_view',
  tracker_created: 'tracker_created',
  tracker_item_checked: 'tracker_item_checked',

  // AI usage (high-level, no prompt/text is ever sent)
  ai_diary_used: 'ai_diary_used',
  ai_calendar_used: 'ai_calendar_used',

  // Navigation / sharing / integrations
  nav_tab_click: 'nav_tab_click',
  share_link_created: 'share_link_created',
  share_link_opened: 'share_link_opened',
  material_link_opened: 'material_link_opened',
  material_added_to_diary: 'material_added_to_diary',
  tg_link_generated: 'tg_link_generated',
  tg_unlinked: 'tg_unlinked',

  // Onboarding
  onboarding_open: 'onboarding_open',
  onboarding_skip: 'onboarding_skip',
  onboarding_save: 'onboarding_save',
  onboarding_step_change: 'onboarding_step_change',
  onboarding_skip_step: 'onboarding_skip_step',
  onboarding_field_change: 'onboarding_field_change',

  // CTAs
  cta_connect_telegram: 'cta_connect_telegram',
  cta_make_first_record: 'cta_make_first_record',
  cta_create_event: 'cta_create_event',
  cta_view_analytics: 'cta_view_analytics',

  // Debug/self-check (sent only when explicitly triggered)
  debug_ping: 'debug_ping',
});

export function isDoNotTrackEnabled() {
  try {
    const nav = typeof navigator !== 'undefined' ? navigator : null;
    const win = typeof window !== 'undefined' ? window : null;
    return (
      (nav && (nav.doNotTrack === '1' || nav.doNotTrack === 'yes' || nav.msDoNotTrack === '1')) ||
      (win && (win.doNotTrack === '1' || win.doNotTrack === 'yes'))
    );
  } catch {
    return false;
  }
}

export function isProdLikeHost() {
  try {
    const host = String(window.location.hostname || '').toLowerCase();
    if (!host) return false;
    // Keep admin, preview hosts and LAN/IP test traffic out of marketing reports.
    return host === 'noesa.ai' || host === 'www.noesa.ai';
  } catch {
    return false;
  }
}


